import { inject, Injectable } from '@angular/core';
import { TranslocoService } from '@jsverse/transloco';
import { Action, State, StateContext } from '@ngxs/store';
import { MessageService } from 'primeng/api';
import { catchError, EMPTY, filter, map, tap } from 'rxjs';

import { ToastSeverityIcons } from '@customer-portal/shared';

import { ServiceMasterListItemModel, SiteMasterListItemModel } from '../models';
import { LanguageListItemModel } from '../models/language.model';
import {
  LanguageListService,
  ServiceMasterListService,
  SiteMasterListService,
} from '../services';
import {
  LoadGlobalLanguagesList,
  LoadGlobalLanguagesListError,
  LoadGlobalServiceMasterList,
  LoadGlobalSiteMasterList,
  ResetGlobalServiceMasterState,
  ResetGlobalSiteMasterState,
  UpdateGlobalSiteNickname,
} from './actions';

export interface GlobalStateModel {
  siteMaster: {
    siteList: SiteMasterListItemModel[];
    loaded: boolean;
    loadingError: string | null;
  };
  serviceMaster: {
    serviceList: ServiceMasterListItemModel[];
    loaded: boolean;
    loadingError: string | null;
  };
  languages: {
    languagesList: LanguageListItemModel[];
    loaded: boolean;
    loadingError: string | null;
  };
}

const defaultState: GlobalStateModel = {
  siteMaster: {
    siteList: [],
    loaded: false,
    loadingError: null,
  },
  serviceMaster: {
    serviceList: [],
    loaded: false,
    loadingError: null,
  },
  languages: {
    languagesList: [],
    loaded: false,
    loadingError: null,
  },
};

@State<GlobalStateModel>({
  name: 'global',
  defaults: defaultState,
})
@Injectable()
export class GlobalState {
  private readonly siteMasterListService = inject(SiteMasterListService);
  private readonly serviceMasterListService = inject(ServiceMasterListService);
  private readonly languageListService = inject(LanguageListService);
  private translocoService = inject(TranslocoService);
  private messageService = inject(MessageService);

  @Action(LoadGlobalSiteMasterList)
  loadGlobalSiteMasterList(ctx: StateContext<GlobalStateModel>) {
    return this.siteMasterListService.getSiteMasterList().pipe(
      map((siteMasterDto) => {
        if (!siteMasterDto || !siteMasterDto.isSuccess) {
          const errorMessage =
            siteMasterDto?.message || 'Failed to load site master data';
          ctx.patchState({
            siteMaster: {
              siteList: [],
              loadingError: errorMessage,
              loaded: true,
            },
          });

          return siteMasterDto;
        }

        return siteMasterDto;
      }),
      filter((siteMasterDto) => siteMasterDto?.isSuccess ?? false),
      tap((siteMasterDto) => {
        ctx.patchState({
          siteMaster: {
            siteList: siteMasterDto?.data ?? [],
            loadingError: null,
            loaded: true,
          },
        });
      }),
    );
  }

  @Action(LoadGlobalServiceMasterList)
  loadGlobalServiceMasterList(ctx: StateContext<GlobalStateModel>) {
    return this.serviceMasterListService.getServiceMasterList().pipe(
      map((serviceMasterDto) => {
        if (!serviceMasterDto || !serviceMasterDto.isSuccess) {
          const errorMessage =
            serviceMasterDto?.message || 'Failed to load service master data';
          ctx.patchState({
            serviceMaster: {
              serviceList: [],
              loadingError: errorMessage,
              loaded: true,
            },
          });

          return serviceMasterDto;
        }

        return serviceMasterDto;
      }),
      filter((serviceMasterDto) => serviceMasterDto?.isSuccess ?? false),
      tap((serviceMasterDto) => {
        ctx.patchState({
          serviceMaster: {
            serviceList: serviceMasterDto?.data ?? [],
            loadingError: null,
            loaded: true,
          },
        });
      }),
    );
  }

  @Action(LoadGlobalLanguagesListError)
  loadGlobalLanguagesListError(
    ctx: StateContext<GlobalStateModel>,
    _action: LoadGlobalLanguagesListError,
  ) {
    ctx.patchState({
      languages: {
        languagesList: [],
        loadingError: 'Failed to load languages list',
        loaded: true,
      },
    });
    this.messageService.add({
      severity: 'error',
      summary: this.translocoService.translate('toast.somethingWentWrong'),
      icon: ToastSeverityIcons.ExclamationCircle,
    });
  }

  @Action(LoadGlobalLanguagesList)
  loadGlobalLanguagesList(ctx: StateContext<GlobalStateModel>) {
    return this.languageListService.getLanguagesList().pipe(
      tap((languageListResponse) => {
        const languagesList = languageListResponse.data ?? [];

        this.translocoService.setAvailableLangs(
          languagesList.map((language) => language.languageCode),
        );

        ctx.patchState({
          languages: {
            languagesList,
            loadingError: null,
            loaded: true,
          },
        });
      }),
      catchError(() => {
        ctx.dispatch(new LoadGlobalLanguagesListError());

        return EMPTY;
      }),
    );
  }

  @Action(ResetGlobalSiteMasterState)
  resetGlobalSiteMasterState(ctx: StateContext<GlobalStateModel>): void {
    ctx.patchState({
      siteMaster: {
        siteList: [],
        loadingError: null,
        loaded: false,
      },
    });
  }

  @Action(UpdateGlobalSiteNickname)
  updateGlobalSiteNickname(
    ctx: StateContext<GlobalStateModel>,
    { siteId, siteNickname }: UpdateGlobalSiteNickname,
  ): void {
    const state = ctx.getState();

    ctx.patchState({
      siteMaster: {
        ...state.siteMaster,
        siteList: state.siteMaster.siteList.map((site) =>
          site.id === siteId ? { ...site, siteNickname } : site,
        ),
      },
    });
  }

  @Action(ResetGlobalServiceMasterState)
  resetGlobalServiceMasterState(ctx: StateContext<GlobalStateModel>): void {
    ctx.patchState({
      serviceMaster: {
        serviceList: [],
        loadingError: null,
        loaded: false,
      },
    });
  }
}
